import { useRef, useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import ConfettiExplosion from '../ui/ConfettiExplosion'

export default function ChorusSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-200px' })
  const [showConfetti, setShowConfetti] = useState(false)

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        setShowConfetti(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isInView])

  return (
    <motion.section
      ref={ref}
      id="chorus"
      className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden px-4 sm:px-6"
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-primary via-primary-pink/10 to-dark-primary" />

        {/* Pulsing rings - fewer and smaller on mobile */}
        {isInView && (
          <>
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-pink/30 sm:border-2"
                initial={{ width: 0, height: 0, opacity: 0 }}
                animate={{
                  width: [0, 400 + i * 150],
                  height: [0, 400 + i * 150],
                  opacity: [0.8, 0]
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.3,
                  repeat: Infinity,
                  ease: 'easeOut'
                }}
              />
            ))}
          </>
        )}

        {/* Floating hearts - fewer on mobile */}
        {isInView && [...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-lg sm:text-2xl md:text-4xl"
            initial={{
              x: `${Math.random() * 100}%`,
              y: '110%',
              rotate: Math.random() * 360,
              opacity: 0
            }}
            animate={{
              y: '-10%',
              opacity: [0, 1, 0],
              rotate: Math.random() * 360 + 180
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              delay: Math.random() * 3,
              repeat: Infinity,
              ease: 'linear'
            }}
          >
            {['💕', '💖', '💗', '💝', '❤️', '💘'][i % 6]}
          </motion.div>
        ))}
      </div>

      {/* Confetti */}
      {showConfetti && <ConfettiExplosion trigger={true} particleCount={100} />}

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl w-full">
        {/* Date Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.5, type: 'spring' }}
          className="inline-flex items-center gap-2 sm:gap-3 px-4 py-2 sm:px-6 sm:py-3 bg-primary-pink/20 backdrop-blur-sm rounded-full border border-primary-pink/30 mb-4 sm:mb-8"
        >
          <span className="text-lg sm:text-2xl">💕</span>
          <span className="text-base sm:text-lg lg:text-xl font-bold text-primary-pink">4 Juin 2025</span>
          <span className="text-lg sm:text-2xl">💕</span>
        </motion.div>

        {/* Main Title */}
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold mb-4 sm:mb-6 lg:mb-8"
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="block text-white mb-1 sm:mb-2">Le Moment</span>
          <motion.span
            className="block gradient-text leading-tight"
            animate={isInView ? {
              textShadow: [
                '0 0 20px rgba(232, 81, 178, 0.5)',
                '0 0 60px rgba(232, 81, 178, 0.8)',
                '0 0 20px rgba(232, 81, 178, 0.5)'
              ]
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Où Tout a Changé
          </motion.span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-light-pink-100 mb-6 sm:mb-8 lg:mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          C'est officiel. On est ensemble.
        </motion.p>

        {/* Highlight Box */}
        <motion.div
          className="inline-block glass-card pink-glow p-4 sm:p-6 lg:p-8 mx-2"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.9, type: 'spring' }}
        >
          <motion.p
            className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white font-medium italic leading-relaxed"
            animate={isInView ? { opacity: [0.7, 1, 0.7] } : {}}
            transition={{ duration: 3, repeat: Infinity }}
          >
            "Quatre juin, c'est officiel maintenant
            <br />
            On est ensemble, bébé, c'est tellement
            <br />
            Plus qu'une date, c'est une promesse
            <br />
            De t'aimer toujours avec tendresse"
          </motion.p>
        </motion.div>

        {/* Hearts Animation */}
        <motion.div
          className="mt-6 sm:mt-8 lg:mt-12 flex justify-center gap-2 sm:gap-3 lg:gap-4 flex-wrap"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2 }}
        >
          {['❤️', '🧡', '💛', '💚', '💙', '💜', '🩷'].map((heart, i) => (
            <motion.span
              key={i}
              className="text-xl sm:text-2xl lg:text-3xl"
              animate={{
                y: [0, -10, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 1,
                delay: i * 0.1,
                repeat: Infinity,
                repeatDelay: 0.5
              }}
            >
              {heart}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}
